import { useState } from "react";
import { View } from "react-native";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Text } from "../common/AppText";
import { NativeButton } from "../common/NativeButton";
import { EmployerDisclosurePreview } from "../jobs/EmployerDisclosurePreview";
import { useTheme } from "../../hooks/useTheme";
import { useLocale } from "../../hooks/useLocale";
import {
  fetchCompanyProfileAccess,
  fetchCompanyProfilePreview,
  grantCompanyProfileAccess,
  revokeCompanyProfileAccess,
  type CompanyProfilePreview,
} from "../../lib/employer-disclosure-api";
export function CompanyLiveProfileAccessPanel() {
  const { t } = useTranslation("common");
  const { theme } = useTheme();
  const { isRTL } = useLocale();
  const client = useQueryClient();
  const access = useQuery({
    queryKey: ["company-profile-access"],
    queryFn: fetchCompanyProfileAccess,
  });
  const [preview, setPreview] = useState<CompanyProfilePreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  async function act(action: () => Promise<unknown>) {
    setBusy(true);
    setError(false);
    try {
      await action();
      await client.invalidateQueries({ queryKey: ["company-profile-access"] });
    } catch {
      setError(true);
      setPreview(null);
    } finally {
      setBusy(false);
    }
  }
  return (
    <View style={{ gap: 12 }}>
      <Text
        style={{
          color: theme.textSecondary,
          textAlign: isRTL ? "right" : "left",
        }}
      >
        {t("settings.employerAccess.description")}
      </Text>
      {access.isPending ? (
        <Text>{t("settings.employerAccess.loading")}</Text>
      ) : null}
      {access.isError || error ? (
        <Text style={{ color: theme.textSecondary }}>
          {t("settings.employerAccess.retryReview")}
        </Text>
      ) : null}
      {access.data?.companies.length === 0 ? (
        <Text style={{ color: theme.textSecondary }}>
          {t("settings.employerAccess.empty")}
        </Text>
      ) : null}
      {access.data?.companies.map((company) => (
        <View key={company.id} style={{ gap: 8 }}>
          <Text
            style={{ color: theme.text, textAlign: isRTL ? "right" : "left" }}
          >
            {company.name} ·{" "}
            {t(
              `settings.employerAccess.${company.active ? "active" : "inactive"}`,
            )}
          </Text>
          {company.canRevoke ? (
            <NativeButton
              title={t("settings.employerAccess.revoke")}
              variant="outline"
              disabled={busy}
              onPress={() =>
                void act(async () => {
                  await revokeCompanyProfileAccess(company.id);
                  setPreview(null);
                })
              }
            />
          ) : null}
          {access.data?.enabled && !company.active ? (
            <NativeButton
              title={t("settings.employerAccess.review")}
              variant="outline"
              disabled={busy}
              onPress={() =>
                void act(async () => {
                  setPreview(await fetchCompanyProfilePreview(company.id));
                })
              }
            />
          ) : null}
        </View>
      ))}
      {preview ? (
        <View style={{ gap: 12 }}>
          <Text style={{ color: theme.text }}>
            {t("settings.employerAccess.consent", {
              company: preview.company.name,
            })}
          </Text>
          <EmployerDisclosurePreview value={preview.projection} expanded />
          <NativeButton
            title={t("settings.employerAccess.grant")}
            loading={busy}
            onPress={() =>
              void act(async () => {
                await grantCompanyProfileAccess(preview);
                setPreview(null);
              })
            }
          />
          <NativeButton
            title={t("settings.employerAccess.cancel")}
            variant="ghost"
            disabled={busy}
            onPress={() => setPreview(null)}
          />
        </View>
      ) : null}
    </View>
  );
}
